import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { Check, ChevronDown } from "lucide-react";

const CLOSE_DURATION_MS = 200;

interface FilterOption<Value extends string> {
  value: Value;
  label: string;
  count?: number;
}

export function FilterPopover<Value extends string>({
  label,
  value,
  options,
  onChange,
  className = "",
  triggerClassName = "",
  columns = 1,
  hideLabel = false,
}: {
  label: string;
  value: Value;
  options: Array<FilterOption<Value>>;
  onChange: (value: Value) => void;
  className?: string;
  triggerClassName?: string;
  columns?: 1 | 2;
  hideLabel?: boolean;
}) {
  const [mounted, setMounted] = useState(false);
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const closeTimer = useRef<number | null>(null);
  const openFrame = useRef<number | null>(null);
  const listboxId = useId();
  const matchedIndex = options.findIndex((option) => option.value === value);
  const selectedIndex = Math.max(0, matchedIndex);
  const selectedLabel =
    matchedIndex >= 0 ? options[matchedIndex]?.label || "" : "";

  const clearScheduledMotion = useCallback(() => {
    if (closeTimer.current !== null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    if (openFrame.current !== null) {
      window.cancelAnimationFrame(openFrame.current);
      openFrame.current = null;
    }
  }, []);

  const closeMenu = useCallback(
    (restoreFocus = false) => {
      clearScheduledMotion();
      setOpen(false);
      closeTimer.current = window.setTimeout(() => {
        closeTimer.current = null;
        setMounted(false);
      }, CLOSE_DURATION_MS);
      if (restoreFocus) triggerRef.current?.focus();
    },
    [clearScheduledMotion],
  );

  const openMenu = useCallback(
    (focusIndex = selectedIndex) => {
      clearScheduledMotion();
      setMounted(true);
      openFrame.current = window.requestAnimationFrame(() => {
        openFrame.current = null;
        setOpen(true);
        optionRefs.current[focusIndex]?.focus();
      });
    },
    [clearScheduledMotion, selectedIndex],
  );

  useEffect(() => {
    if (!mounted) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) closeMenu();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [closeMenu, mounted]);

  useEffect(
    () => () => {
      clearScheduledMotion();
    },
    [clearScheduledMotion],
  );

  const focusOption = (index: number) => {
    const nextIndex = (index + options.length) % options.length;
    optionRefs.current[nextIndex]?.focus();
  };

  const onMenuKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu(true);
      return;
    }
    const activeIndex = optionRefs.current.findIndex(
      (option) => option === document.activeElement,
    );
    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusOption(activeIndex + columns);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusOption(activeIndex - columns);
    } else if (columns === 2 && event.key === "ArrowRight") {
      event.preventDefault();
      focusOption(activeIndex + 1);
    } else if (columns === 2 && event.key === "ArrowLeft") {
      event.preventDefault();
      focusOption(activeIndex - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusOption(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusOption(options.length - 1);
    } else if (event.key === "Tab") {
      closeMenu();
    }
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={mounted ? listboxId : undefined}
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={(event) => {
          if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openMenu(event.key === "ArrowUp" ? options.length - 1 : 0);
          }
        }}
        className={`flex min-h-8 items-center gap-2 rounded-full border bg-paper px-4 py-1.5 text-[12.5px] text-ink-soft transition-[border-color,box-shadow,color] duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-clay/20 ${open ? "border-clay/55 text-ink shadow-[0_5px_18px_rgba(46,42,37,0.08)]" : "border-sand-light hover:border-sand"} ${triggerClassName}`}
      >
        <span className={hideLabel ? "sr-only" : undefined}>{label}</span>
        <span className="font-medium text-ink">{selectedLabel}</span>
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={`text-ink-faint transition-transform duration-200 motion-reduce:transition-none ${open ? "rotate-180" : ""}`}
        />
      </button>

      {mounted ? (
        <div
          id={listboxId}
          role="listbox"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className={`absolute top-[calc(100%+8px)] z-30 min-w-36 rounded-2xl border border-sand-light/90 bg-paper p-1.5 shadow-[0_18px_48px_-16px_rgba(46,42,37,0.3)] transition-[opacity,transform] duration-200 ease-out motion-reduce:transform-none motion-reduce:transition-none ${columns === 2 ? "left-0 grid w-full origin-top-left grid-cols-2 gap-1" : "right-0 origin-top-right"} ${open ? "translate-y-0 scale-100 opacity-100" : "pointer-events-none -translate-y-1 scale-[0.98] opacity-0"}`}
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <button
                key={option.value}
                ref={(element) => {
                  optionRefs.current[index] = element;
                }}
                id={`${listboxId}-${option.value}`}
                type="button"
                role="option"
                aria-selected={selected}
                tabIndex={-1}
                onClick={() => {
                  if (!selected) onChange(option.value);
                  closeMenu(true);
                }}
                className={`flex w-full items-center justify-between gap-3 rounded-xl px-3 py-2 text-left text-[12.5px] font-medium transition-[background-color,color,transform] duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-clay/20 active:scale-[0.98] motion-reduce:transform-none motion-reduce:transition-none ${selected ? "bg-peach text-clay-deep" : "text-ink-soft hover:bg-cream hover:text-ink"}`}
              >
                <span className="min-w-0 truncate">{option.label}</span>
                <span className="flex min-w-9 shrink-0 items-center justify-end">
                  {selected ? (
                    <Check size={13} aria-hidden="true" />
                  ) : option.count !== undefined ? (
                    <span className="font-mono text-[10.5px] font-normal tabular-nums text-ink-faint">
                      ({option.count})
                    </span>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
